import React from 'react';
import {Alert, Button} from 'reactstrap';
import {FlaskConical} from 'lucide-react';
import {toast} from 'react-toastify';
import RoleNames from '../../../lib/RoleNames';
import {useAuthStateManager} from '../../../hooks/useAuthStateManager';
import useConfirm from '../../../hooks/useConfirm';
import unknownErrorToString from '../../../lib/unknownErrorToString';
import {
    useGettingStarted,
    useSampleData
} from '../../../api/gettingStarted/useGettingStarted';

/**
 * Across the app while sample data is loaded, so nobody mistakes it for the
 * real plan. Administrators can clear it from here.
 */
const SampleDataBanner: React.FC = () => {
    const {isInRole} = useAuthStateManager();
    const isCustomerUser = isInRole(RoleNames.CustomerUser);
    const isAdministrator = isInRole(RoleNames.Administrator);
    const {data: status} = useGettingStarted(isCustomerUser);
    const sampleData = useSampleData();
    const {confirm, confirmModal} = useConfirm();

    if (!status?.sampleDataLoaded) {
        return null;
    }

    const clear = () =>
        confirm(
            <>
                Remove the sample people, teams, job roles, tags and projects,
                and everything attached to them? Anything you added or changed
                on sample records goes too. Your own records stay.
            </>,
            () =>
                sampleData.mutate(
                    {load: false},
                    {
                        onSuccess: () => toast.success('Sample data cleared.'),
                        onError: error =>
                            toast.error(unknownErrorToString(error))
                    }
                ),
            {title: 'Clear the sample data?'}
        );

    return (
        <>
            {confirmModal}
            <Alert
                color="secondary"
                className="mb-0 rounded-0 py-2 d-flex align-items-center gap-2 flex-wrap"
                fade={false}
            >
                <FlaskConical size={16} />
                <span>
                    You&rsquo;re looking at <strong>sample data</strong>.
                    {isAdministrator
                        ? ' Clear it when you are ready to add your own.'
                        : ' An administrator will clear it before real use.'}
                </span>
                {isAdministrator && (
                    <Button
                        size="sm"
                        color="secondary"
                        outline
                        className="ms-auto"
                        disabled={sampleData.isPending}
                        onClick={clear}
                    >
                        Clear sample data
                    </Button>
                )}
            </Alert>
        </>
    );
};

export default SampleDataBanner;
